import {
  breakpoints,
  Icons,
  SkeletonLoader,
  SkeletonUnit,
  useHooks,
} from '@everlywell/leaves';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import StripePayment from 'components/Stripe/StripePayment';
import { flatten } from 'lodash-es';
import React, { useState, useEffect, useRef } from 'react';
import { useMutation } from 'react-query';
import { skusForMixpanel } from 'utils/analytics';
import {
  applyPromoCode,
  removeAllPromos,
  removeLineItem,
  updateLineItemQuantity,
} from 'utils/api/cartApis';
import { currentOrderGrandTotalInCents } from 'utils/cartHelpers';
import { ANALYTICS } from 'utils/constants/analytics';
import { formatPrice } from 'utils/helpers';
import { includesCovidDTC } from 'utils/helpers/covid';
import { stripePublicKey } from 'utils/helpers/paymentHelpers';
import { LineItem, FormFieldType, AxiosErrorResponse } from 'utils/types';

import emptyCartImage from '../../images/empty-cart.svg';
import { useCart } from './Cart.hooks';
import * as S from './Cart.styles';
import CartProduct from './CartProduct';
import { Chip } from './Chip';
import ErrorText from './ErrorText';
import Input from './Input';

// Make sure to call `loadStripe` outside of a component’s render to avoid
// recreating the `Stripe` object on every render.
// const stripePromise = loadStripe(stripePublicKey);

type CartProps = {
  showAltPayments: boolean;
};

/**
 * Cart is the experience of showing the user what is in their order as a floating
 * side window
 */
const Cart = (props: CartProps) => {
  const {
    openCart,
    closeCart,
    isCartOpen,
    order,
    isFetchingOrder,
    refetchOrder,
    errors,
    resetOrder,
  } = useCart();

  const [showTOS, setShowTOS] = useState(false);

  const { showAltPayments } = props;

  const currentOrder = order;
  const lineItems = order?.line_items ?? [];

  const cartTotal = {
    totalPrice: currentOrder ? +currentOrder.total : 0,
    subTotal: currentOrder ? +currentOrder.amount : 0,
    productQuantity:
      currentOrder?.line_items?.reduce((acc, item) => acc + item.quantity, 0) ||
      0,
    currencyId: 'USD',
    currencyFormat: '$',
  };

  const isOpen = isCartOpen;
  const openFloatCart = openCart;
  const closeFloatCart = closeCart;

  /**
   * Remove a line item from the cart.
   */
  const {
    mutate: _removeItemFromCart,
    isLoading: isRemovingItemFromCart,
    error: removeItemFromCartError,
  } = useMutation(removeLineItem, {
    onSuccess: () => {
      refetchOrder?.();
    },
    onError: (error: AxiosErrorResponse) => {
      if (error.response?.status === 404) {
        resetOrder();
      }
    },
  });

  const removeProduct = (lineItem: LineItem) =>
    currentOrder &&
    lineItem.id &&
    _removeItemFromCart({
      orderId: currentOrder?.id,
      lineItemId: lineItem.id,
    });

  /**
   * Change the quantity of a line item in the cart.
   */
  const {
    mutate: _updateLineItemQuantity,
    isLoading: isUpdatingLineItemQuantity,
    error: updateLineItemQuantityError,
  } = useMutation(updateLineItemQuantity, {
    onSuccess: () => {
      refetchOrder?.();
    },
    onError: (error: AxiosErrorResponse) => {
      if (error.response?.status === 404) {
        resetOrder();
      }
    },
  });

  const changeProductQuantity = (lineItem: LineItem) =>
    currentOrder &&
    lineItem.id &&
    _updateLineItemQuantity({
      orderId: currentOrder?.id,
      variantId: lineItem.variant_id,
      quantity: lineItem.quantity,
    });

  /**
   * Handle promo codes
   */

  const [promoInputValue, setPromoInputValue] = useState('');
  const [showPromoInput, setShowPromoInput] = useState(false);

  const togglePromoInputVisibility = () =>
    setShowPromoInput((prevValue) => !prevValue);

  const handlePromoChange = (formfield: FormFieldType) => {
    const { value } = formfield;
    setPromoInputValue(value);
  };

  /**
   * Apply a promo code to the cart.
   */
  const {
    mutate: _applyPromoCode,
    isLoading: isApplyingPromoCode,
    error: applyPromoCodeError,
  } = useMutation(applyPromoCode, {
    onSuccess: () => {
      refetchOrder?.().then(() => {
        setPromoInputValue('');
        setShowPromoInput(false);
      });
    },
    onError: (error: AxiosErrorResponse) => {
      if (error.response?.status === 404) {
        resetOrder();
      }
    },
  });

  const handlePromoApply = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    currentOrder &&
      _applyPromoCode({ orderId: currentOrder.id, code: promoInputValue });
  };

  const currentPromoCode = currentOrder?.promotions[0]?.code || '';

  const isPromoApplied = Boolean(currentPromoCode);

  /**
   * Remove a promo code from the cart.
   */
  const {
    mutate: _removePromoCode,
    isLoading: isRemovingPromoCode,
    error: removePromoCodeError,
  } = useMutation(removeAllPromos, {
    onSettled: () => {
      refetchOrder?.();
    },
    onError: (error: AxiosErrorResponse) => {
      if (error.response?.status === 404) {
        resetOrder();
      }
    },
  });

  const handleRemovePromo = () =>
    currentOrder && _removePromoCode({ orderId: currentOrder.id });

  const cartErrors = flatten([
    ...errors,
    (removeItemFromCartError as AxiosErrorResponse)?.response?.data.errors,
    (updateLineItemQuantityError as AxiosErrorResponse)?.response?.data.errors,
  ]).filter(Boolean);
  const promoErrors = flatten([
    (applyPromoCodeError as AxiosErrorResponse)?.response?.data.errors,
    (removePromoCodeError as AxiosErrorResponse)?.response?.data.errors,
  ]).filter(Boolean);

  const showLoading =
    isFetchingOrder ||
    isRemovingItemFromCart ||
    isUpdatingLineItemQuantity ||
    isApplyingPromoCode ||
    isRemovingPromoCode;

  /**
   *
   */

  const products = lineItems.map((p: LineItem) => (
    <CartProduct
      product={p}
      removeProduct={removeProduct}
      changeProductQuantity={changeProductQuantity}
      key={p.variant_id}
    />
  ));
  const checkoutRef = useRef<HTMLAnchorElement>(null);
  const [stripePromise, setStripePromise] =
    useState<Promise<Stripe | null> | null>(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const { width } = useHooks.useWindowSize();
  const isCartFullscreen = width && width <= breakpoints.forTabletHorizontalUp;
  useHooks.useDisableScroll(isOpen && !!isCartFullscreen);

  const hasOnlyLabVisit = lineItems.every((item) => item.is_lab_visit);
  const shipping = parseFloat(currentOrder?.shipping || '');
  const totalPrice = cartTotal.totalPrice - shipping;

  useEffect(() => {
    setHasLoaded(true);
  }, [stripePromise]);

  useEffect(() => {
    if (isOpen && !stripePromise) setStripePromise(loadStripe(stripePublicKey));
    if (checkoutRef?.current) {
      isOpen ? checkoutRef.current.focus() : checkoutRef.current.blur();
    }
  }, [isOpen, stripePromise]);

  if (!hasLoaded) return null;
  const variantsSkus = skusForMixpanel(currentOrder?.line_items);

  return order ? (
    <>
      <S.OpenCartBtn
        tabIndex={-1}
        data-testid="open-cart"
        aria-label="Open Cart"
        onClick={() => openFloatCart()}
      >
        {cartTotal ? (
          <S.CartInfoWrapper tabIndex={0}>
            <Icons.Cart />
            {products.length > 0 ? (
              <S.CartQuantity data-testid="cart-quantity">
                {cartTotal.productQuantity}
              </S.CartQuantity>
            ) : null}
          </S.CartInfoWrapper>
        ) : null}
      </S.OpenCartBtn>

      <S.CartContentOverlay
        showOverlay={isOpen && isCartFullscreen}
        aria-expanded={isOpen ? true : false}
      >
        <S.CartContent
          aria-labelledby="cart-title"
          aria-live="polite"
          data-testid="cart-content"
          id="cart-content"
          isOpen={isOpen}
          role="dialog"
          tabIndex={0}
        >
          <S.CartHeader>
            <S.CartCloseBtn
              tabIndex={isOpen ? 0 : -1}
              data-testid="close-cart"
              aria-label="Close Cart"
              onClick={() => closeFloatCart()}
            >
              <Icons.Close />
            </S.CartCloseBtn>
            <S.CartTitle id="cart-title" aria-label="Your Cart">
              Your Cart
            </S.CartTitle>
          </S.CartHeader>

          <S.ShelfContainer>
            {!products.length && !showLoading ? (
              <S.EmptyCart data-testid="empty-cart">
                <S.EmptyCartImg
                  alt="empty cart"
                  src={emptyCartImage}
                  width={114}
                  height={80}
                />
                <p>Your shopping cart is empty</p>
              </S.EmptyCart>
            ) : (
              <>
                {cartErrors
                  ? flatten([cartErrors]).map((err) => (
                      <ErrorText>{err}</ErrorText>
                    ))
                  : null}
                {products}

                {!isPromoApplied && products.length ? (
                  <S.PromoWrapper data-testid="cart-promo-wrapper">
                    <S.PromoApplyLink
                      onClick={() => togglePromoInputVisibility()}
                      data-testid="cart-promo-link"
                    >
                      Have a promo code?
                      <S.PromoExpandIcon
                        shouldShowPromoInput={showPromoInput}
                      />
                    </S.PromoApplyLink>
                    <S.InputWrapper shouldShowPromoInput={showPromoInput}>
                      <form
                        onSubmit={handlePromoApply}
                        data-testid="cart-promo-form"
                      >
                        <Input
                          name="promo-input"
                          placeholder="Enter Promo Code"
                          onChange={handlePromoChange}
                          value={promoInputValue}
                          shouldFocus={showPromoInput}
                          onSubmit={() => {}}
                          data-testid="cart-promo-input"
                          submitting={isApplyingPromoCode}
                        />
                      </form>
                    </S.InputWrapper>
                  </S.PromoWrapper>
                ) : null}
                {promoErrors
                  ? promoErrors.map((err) => <ErrorText>{err}</ErrorText>)
                  : null}
                {products.length ? (
                  <S.CartCalcWrapper>
                    <S.LabelWrapper>
                      <S.PriceLabel>Subtotal:</S.PriceLabel>
                      {hasOnlyLabVisit ? null : (
                        <S.PriceLabel>Shipping:</S.PriceLabel>
                      )}
                      {isPromoApplied ? (
                        <S.AppliedPromoWrapper
                          code={currentPromoCode}
                          data-testid="cart-applied-promo-wrapper"
                        >
                          <S.PriceLabel>Applied:</S.PriceLabel>
                          {currentOrder?.promotions &&
                          currentOrder.promotions.length > 0 ? (
                            <Chip
                              onRemove={handleRemovePromo}
                              data-testid="cart-applied-promo-code"
                            >
                              {currentOrder.promotions[0].code}
                            </Chip>
                          ) : null}
                        </S.AppliedPromoWrapper>
                      ) : null}
                    </S.LabelWrapper>
                    <S.ValueWrapper>
                      <S.Price data-testid="cart-subtotal">
                        ${formatPrice(cartTotal.subTotal)}
                      </S.Price>
                      {hasOnlyLabVisit ? null : <S.Price>FREE</S.Price>}
                      {isPromoApplied ? (
                        <S.DiscountAmount>
                          <span>
                            -$
                            {formatPrice(
                              currentOrder
                                ? parseFloat(
                                    currentOrder.promo_total.replace('-', ''),
                                  )
                                : 0,
                            )}
                          </span>
                        </S.DiscountAmount>
                      ) : null}
                    </S.ValueWrapper>
                  </S.CartCalcWrapper>
                ) : null}
              </>
            )}
          </S.ShelfContainer>
          {showLoading ? (
            <S.LoadingScreen data-testid="cart-loading-screen" />
          ) : null}

          {cartTotal ? (
            <S.Footer>
              <S.TotalWrapper>
                <S.TotalLabel>
                  Total ({cartTotal.productQuantity} Item
                  {cartTotal.productQuantity > 1 ? 's' : ''}
                  ):
                </S.TotalLabel>
                <S.TotalPrice data-testid="cart-total-price">
                  {totalPrice ? `$${formatPrice(totalPrice)}` : '$0.00'}
                </S.TotalPrice>
              </S.TotalWrapper>
              <S.CheckoutLink
                role="button"
                aria-label="Checkout"
                // Gatsby link is traslated to HTMLAnchorElement
                // @ts-ignore
                ref={checkoutRef}
                to={'/checkout'}
                data-testid="checkout-link"
                aria-disabled={!products.length}
                disableBtn={!products.length || showLoading}
                tabIndex={products.length ? 0 : -1}
              >
                Proceed to Checkout
              </S.CheckoutLink>
              {showAltPayments &&
              !includesCovidDTC(currentOrder?.line_items) ? (
                <Elements stripe={stripePromise}>
                  <StripePayment
                    cartTotal={currentOrderGrandTotalInCents(totalPrice)}
                    cartNotEmpty={
                      currentOrder ? currentOrder.line_items.length > 0 : false
                    }
                    variantsSkus={variantsSkus}
                    setTOS={setShowTOS}
                    isInCart={true}
                    label={ANALYTICS.LABELS.EXPRESS_PAY_CART}
                  />
                </Elements>
              ) : null}
              {showTOS ? (
                <S.Agreement>
                  <span>By completing your purchase you agree to our </span>
                  <a
                    href="//www.everlywell.com/terms-of-use/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Terms of Service.
                  </a>
                </S.Agreement>
              ) : null}
            </S.Footer>
          ) : null}
        </S.CartContent>
      </S.CartContentOverlay>
    </>
  ) : (
    <S.CartSkeletonWrapper>
      <SkeletonLoader
        width={{
          value: 28,
          unit: SkeletonUnit.Pixel,
        }}
        height={{
          value: 28,
          unit: SkeletonUnit.Pixel,
        }}
      ></SkeletonLoader>
    </S.CartSkeletonWrapper>
  );
};

export { Cart };

export default Cart;
